import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { Subject, takeUntil } from 'rxjs';
import { CaregiverFamilyRating, CaregiverFamilyRatingSummary } from '../../models/caregiver-family-rating.model';
import { FamilyRatingService } from '../../services/family-rating-service/family-rating.service';

interface AverageField {
  id: string;
  label: string;
  key: keyof Pick<CaregiverFamilyRatingSummary,
    'serviceScoreAverage' | 'punctualityAverage' | 'empathyAverage' | 'educationAverage' | 'communicationAverage'>;
}

/**
 * Sezione read-only "Valutazioni Famiglie" del tab Valutazione (TEAM-1033).
 * Mostra le medie e lo storico dei rating lasciati dalle famiglie: nulla qui è
 * modificabile e il Salva della pagina non tocca questi dati.
 */
@Component({
  selector: 'app-family-ratings',
  templateUrl: './family-ratings.component.html',
  styleUrls: ['./family-ratings.component.scss'],
  standalone: true,
  imports: [CommonModule, MatExpansionModule]
})
export class FamilyRatingsComponent implements OnChanges, OnDestroy {
  @Input() docRef!: string;

  readonly averageFields: AverageField[] = [
    { id: 'familyServiceScore', label: 'Valutazione servizio', key: 'serviceScoreAverage' },
    { id: 'familyPunctuality', label: 'Puntualità', key: 'punctualityAverage' },
    { id: 'familyEmpathy', label: 'Empatia', key: 'empathyAverage' },
    { id: 'familyEducation', label: 'Educazione', key: 'educationAverage' },
    { id: 'familyCommunication', label: 'Capacità comunicativa', key: 'communicationAverage' },
  ];

  summary: CaregiverFamilyRatingSummary | null = null;
  ratings: CaregiverFamilyRating[] = [];
  loading = false;
  errorMessage: string | null = null;
  /** Il backend risponde 403 ai ruoli diversi da ADMIN / FAMILYTUTOR: in quel caso la sezione non si mostra. */
  forbidden = false;

  private unsubscribe = new Subject<void>();

  constructor(private familyRatingService: FamilyRatingService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['docRef'] && this.docRef) {
      this.load();
    }
  }

  load() {
    this.loading = true;
    this.errorMessage = null;
    this.familyRatingService.getAllRatings(this.docRef)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (summary) => {
          this.summary = summary;
          this.ratings = [...(summary.ratings ?? [])]
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
          this.loading = false;
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.summary = null;
          this.ratings = [];
          if (err.status === 403) {
            this.forbidden = true;
          } else if (err.status !== 404) {
            // 404 = caregiver senza email: equivale a nessuna valutazione
            this.errorMessage = 'Non è stato possibile caricare le valutazioni delle famiglie.';
          }
        }
      });
  }

  average(field: AverageField): number | null {
    return this.summary?.[field.key] ?? null;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
